import { Button } from "@/components/ui/button";
import { ClosingCostsBreakdown, fmt } from "@/hooks/useHomeBuyingSim";
import { ArrowLeft } from "lucide-react";

interface Props {
  costs: ClosingCostsBreakdown;
  downPayment: number;
  cashToClose: number;
  liquidBefore: number;
  liquidAfter: number;
  onContinue: () => void;
  onBack: () => void;
}

function Row({ label, value, highlight, warn }: { label: string; value: string; highlight?: boolean; warn?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${warn ? 'text-destructive' : highlight ? 'text-primary' : 'text-foreground'}`}>{value}</span>
    </div>
  );
}

export default function ClosingCostsScreen({ costs, downPayment, cashToClose, liquidBefore, liquidAfter, onContinue, onBack }: Props) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Closing Costs</h2>
        <p className="text-sm text-muted-foreground">Buying a house requires more than just a down payment.</p>
      </div>
      <div className="glass-card p-4 rounded-xl space-y-2">
        <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider mb-2">Estimated Closing Costs</p>
        <Row label="Lender Fees" value={fmt(costs.lenderFees)} />
        <Row label="Appraisal / Inspection" value={fmt(costs.appraisalInspection)} />
        <Row label="Title / Legal Fees" value={fmt(costs.titleLegal)} />
        <Row label="Taxes / Prepaids" value={fmt(costs.taxesPrepaids)} />
        <Row label="Insurance Setup" value={fmt(costs.insuranceSetup)} />
        <div className="border-t border-border pt-2 mt-2">
          <Row label="Total Closing Costs" value={fmt(costs.total)} highlight />
        </div>
      </div>
      <div className="glass-card p-4 rounded-xl space-y-2">
        <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider mb-2">Cash Summary</p>
        <Row label="Down Payment" value={fmt(downPayment)} />
        <Row label="Closing Costs" value={fmt(costs.total)} />
        <div className="border-t border-border pt-2 mt-2">
          <Row label="Total Cash Needed to Close" value={fmt(cashToClose)} highlight />
        </div>
        <div className="border-t border-border pt-2 mt-2">
          <Row label="Liquid Cash (Before)" value={fmt(liquidBefore)} />
          <Row label="Remaining After Purchase" value={fmt(liquidAfter)} warn={liquidAfter < 0} />
        </div>
      </div>
      {liquidAfter < 0 && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
          <p className="text-xs text-destructive">You do not currently have enough available cash for this purchase path. Try a lower-priced home, a different down payment, or delay the purchase.</p>
        </div>
      )}
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Button onClick={onContinue} className="flex-1">Continue to Mortgage Options</Button>
      </div>
    </div>
  );
}
